
import React, { useState } from 'react';
import { Download, Eye, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useQuery } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useDocumentGeneration } from '@/hooks/useDocumentGeneration';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface Certificate {
  id: number;
  user_id: string;
  unique_code: string;
  issued_at: string;
  certificate_number?: string;
}

interface UserCertificatesSectionProps {
  userId: string;
}

export function UserCertificatesSection({ userId }: UserCertificatesSectionProps) {
  const { toast } = useToast();
  const { generateDocument, logDocumentAccess, isLoading } = useDocumentGeneration();
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState<Certificate | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [documentId, setDocumentId] = useState<string | null>(null);
  
  const { data: certificates = [], isLoading: isLoadingCertificates } = useQuery({
    queryKey: ['user-certificates', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('certificates')
        .select('*')
        .eq('user_id', userId);

      if (error) throw error;
      return data as Certificate[];
    },
    enabled: !!userId,
  });

  const { data: userProfile } = useQuery({
    queryKey: ['user-profile', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });

  // Certificate view, download and email handlers
  const handleView = async (certificate: Certificate) => {
    setSelectedCertificate(certificate);
    
    if (!previewUrl || selectedCertificate?.id !== certificate.id) {
      await handleGenerateCertificate(certificate);
    } else {
      if (documentId) {
        await logDocumentAccess(documentId, 'view');
      }
      setPreviewDialogOpen(true);
    }
  };

  const handleDownload = async (certificate: Certificate) => {
    const studentName = userProfile?.full_name || 'Student';
    
    generateDocument({
      template: 'standard',
      documentType: 'certificate',
      data: {
        certificateId: certificate.id,
        certificateNumber: certificate.certificate_number || certificate.unique_code,
        studentName,
        issuedAt: certificate.issued_at,
        uniqueCode: certificate.unique_code,
        userId: certificate.user_id,
        driverLicense: userProfile?.driver_license_number,
        driverLicenseState: userProfile?.driver_license_state,
        county: userProfile?.county,
        email: userProfile?.email
      },
      fileName: `certificate-${certificate.unique_code}.pdf`
    }, (result) => {
      if (result.success && result.fileUrl && result.documentId) {
        logDocumentAccess(result.documentId, 'download');
        
        const a = document.createElement('a');
        a.href = result.fileUrl;
        a.download = `certificate-${certificate.unique_code}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        
        toast({
          title: "Certificate Downloaded",
          description: `Certificate ${certificate.unique_code} has been downloaded`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: result.error || "Failed to generate certificate for download",
        });
      }
    });
  };

  const handleResendEmail = async (certificate: Certificate) => {
    try {
      toast({
        title: "Sending Email",
        description: "Preparing to send certificate email...",
      });
      
      if (!documentId || selectedCertificate?.id !== certificate.id) {
        await handleGenerateCertificate(certificate);
      }
      
      const { error } = await supabase.functions.invoke('send-admin-email', {
        body: {
          title: "Your Certificate",
          message: `Your certificate ${certificate.unique_code} has been resent to your email address.`,
          eventType: "certificate_resent",
          relatedEntityType: "certificate",
          relatedEntityId: certificate.id.toString(),
          metadata: {
            certificateId: certificate.id,
            certificateCode: certificate.unique_code,
            fileUrl: previewUrl,
            userId: certificate.user_id
          }
        }
      });
      
      if (error) throw error;
      
      if (documentId) {
        await logDocumentAccess(documentId, 'share');
      }
      
      toast({
        title: "Email Sent",
        description: `Certificate ${certificate.unique_code} has been resent`,
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to resend certificate email",
      });
    }
  };

  const handleGenerateCertificate = async (certificate: Certificate) => {
    const studentName = userProfile?.full_name || 'Student';
    
    generateDocument({
      template: 'standard',
      documentType: 'certificate',
      data: {
        certificateId: certificate.id,
        certificateNumber: certificate.certificate_number || certificate.unique_code,
        studentName,
        issuedAt: certificate.issued_at,
        uniqueCode: certificate.unique_code,
        userId: certificate.user_id,
        driverLicense: userProfile?.driver_license_number,
        driverLicenseState: userProfile?.driver_license_state,
        county: userProfile?.county,
        email: userProfile?.email
      },
      fileName: `certificate-${certificate.unique_code}.pdf`
    }, (result) => {
      if (result.success && result.fileUrl) {
        setPreviewUrl(result.fileUrl);
        if (result.documentId) {
          setDocumentId(result.documentId);
        }
        setSelectedCertificate(certificate);
        setPreviewDialogOpen(true);
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: result.error || "Failed to generate certificate",
        });
      }
    });
  };

  if (isLoadingCertificates) {
    return <div>Loading certificates...</div>;
  }

  return (
    <Card>
      <CardContent className="p-6">
        <h3 className="text-lg font-semibold mb-4">Certificates</h3>
        {certificates.length > 0 ? (
          <div className="space-y-4">
            {certificates.map((cert) => (
              <div key={cert.id} className="flex items-center justify-between border-b pb-4 last:border-0">
                <div>
                  <p className="font-medium">Certificate #{cert.unique_code}</p>
                  <p className="text-sm text-muted-foreground">
                    Issued: {new Date(cert.issued_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleView(cert)}
                    disabled={isLoading}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    View
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownload(cert)}
                    disabled={isLoading}
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Download
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleResendEmail(cert)}
                    disabled={isLoading}
                  >
                    <Send className="h-4 w-4 mr-1" />
                    Resend
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground">No certificates issued yet.</p>
        )}
      </CardContent>
      
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Certificate Preview</DialogTitle>
            <DialogDescription>
              Certificate #{selectedCertificate?.unique_code}
            </DialogDescription>
          </DialogHeader>
          {previewUrl && (
            <div className="w-full h-[70vh] overflow-auto">
              <iframe 
                src={previewUrl} 
                title="Certificate Preview"
                className="w-full h-full border-0" 
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
